import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { IMAGES } from "@/data/site";

const SHOTS = [
  { src: IMAGES.truss, alt: "Head-on, crossing a steel truss bridge", caption: "01 / CROSSING", span: "md:col-span-5", tilt: -1.1 },
  { src: IMAGES.camp, alt: "Tent deployed in deep forest, door open", caption: "02 / CAMP", span: "md:col-span-7", off: "md:mt-10", tilt: 0.9 },
  { src: IMAGES.hero, alt: "Syncro front three-quarter before Mount Shasta", caption: "03 / SHASTA", span: "md:col-span-7", tilt: -0.9 },
  { src: IMAGES.desert, alt: "Van at rest under a coastal sunset", caption: "04 / AT REST", span: "md:col-span-5", off: "md:mt-8", tilt: 1.1 },
  { src: IMAGES.coastSunset, alt: "Front three-quarter on a coastal bluff at sunset", caption: "05 / COAST", span: "md:col-span-6", tilt: 0.8 },
  { src: IMAGES.bridge, alt: "Rear view at the rail trestle bridge", caption: "06 / RANGE", span: "md:col-span-6", off: "md:mt-10", tilt: -1.2 },
  { src: IMAGES.redwoods, alt: "Bike rack loaded, parked among the redwoods", caption: "07 / REDWOODS", span: "md:col-span-6", tilt: 0.9 },
  { src: IMAGES.moabView, alt: "Morning view from the rooftop tent over Moab red rock", caption: "08 / MOAB, FROM THE TENT", span: "md:col-span-6", off: "md:mt-6", tilt: -1 },
];

export default function Gallery() {
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (open === null) return;
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen((o) => (o + 1) % SHOTS.length);
      if (e.key === "ArrowLeft") setOpen((o) => (o - 1 + SHOTS.length) % SHOTS.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <section
      id="gallery"
      className="py-16 md:py-28 border-t border-border"
      style={{ backgroundColor: "#EDE5D3", backgroundImage: "url(\"/topo-sierras.png\")" }}
      data-testid="gallery-section"
    >
      <div className="container mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-wrap items-end justify-between gap-6 mb-10 md:mb-14"
        >
          <h2 className="font-headings uppercase text-5xl md:text-7xl leading-[0.85]">
            Field<br /><span className="text-primary">Record</span>
          </h2>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground max-w-xs">
            Eight frames from the road — all owner photography, all this van
          </p>
        </motion.div>

        <div className="grid md:grid-cols-12 gap-4 md:gap-5 items-start">
          {SHOTS.map((s, i) => (
            <motion.figure
              key={s.caption}
              initial={{ opacity: 0, y: 50, rotate: s.tilt }}
              whileInView={{ opacity: 1, y: 0, rotate: s.tilt }}
              whileHover={{ rotate: 0, scale: 1.015 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`group col-span-1 cursor-pointer ${s.span} ${s.off || ""}`}
              onClick={() => setOpen(i)}
              role="button"
              aria-label={`Open ${s.caption} full size`}
              data-testid={`gallery-item-${i}`}
            >
              <div className="bg-card p-2 md:p-3 border border-border shadow-[0_3px_14px_rgba(26,26,24,0.14)]">
                <div className="overflow-hidden">
                  <img
                    src={s.src}
                    alt={s.alt}
                    loading="lazy"
                    style={s.zoom ? { transform: `scale(${s.zoom})`, transformOrigin: s.origin || "50% 50%" } : undefined}
                    className="w-full h-auto img-warm"
                  />
                </div>
              </div>
              <figcaption className="mt-2 font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-muted-foreground">
                {s.caption}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[200] bg-foreground/90 backdrop-blur-md flex flex-col items-center justify-center p-4 md:p-12"
            onClick={() => setOpen(null)}
            data-testid="lightbox-overlay"
          >
            <button
              onClick={() => setOpen(null)}
              className="absolute top-5 right-5 w-11 h-11 border border-background/40 text-background flex items-center justify-center hover:bg-primary hover:border-primary transition-colors duration-300"
              aria-label="Close"
              data-testid="lightbox-close-button"
            >
              <X className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setOpen((o) => (o - 1 + SHOTS.length) % SHOTS.length); }}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 border border-background/40 text-background flex items-center justify-center hover:bg-primary hover:border-primary transition-colors duration-300"
              aria-label="Previous photo"
              data-testid="lightbox-prev-button"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setOpen((o) => (o + 1) % SHOTS.length); }}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 border border-background/40 text-background flex items-center justify-center hover:bg-primary hover:border-primary transition-colors duration-300"
              aria-label="Next photo"
              data-testid="lightbox-next-button"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <motion.img
              key={SHOTS[open].src}
              initial={{ scale: 0.94, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              src={SHOTS[open].src}
              alt={SHOTS[open].alt}
              className="max-h-[82vh] max-w-full w-auto border-[6px] border-card shadow-2xl"
              data-testid="lightbox-image"
            />
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.3em] text-background/70" data-testid="lightbox-caption">
              {SHOTS[open].caption}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
